import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { ImageUploader } from '../../components/ImageUploader';
import { api, type ApiSuccess } from '../../lib/api';

interface Credential {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string;
  category: 'LICENSE' | 'COMMERCIAL_REGISTER' | 'CERTIFICATE' | 'OTHER';
  sortOrder: number;
  active: boolean;
  issuedBy: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  referenceNo: string | null;
}

const CATEGORY: Record<Credential['category'], { label: string; color: 'navy' | 'gold' | 'green' | 'amber' }> = {
  LICENSE:             { label: '🪪 رخصة',         color: 'navy' },
  COMMERCIAL_REGISTER: { label: '📋 سجل تجاري',    color: 'gold' },
  CERTIFICATE:         { label: '🏅 شهادة',        color: 'green' },
  OTHER:               { label: '📄 أخرى',         color: 'amber' },
};

/**
 * 🛡️ إدارة وثائق وشهادات شركة صورة العقارية
 *  - رفع صورة + اسم
 *  - تفعيل/تعطيل
 *  - حذف
 *  - تصنيف + ترتيب
 */
export function AdminCredentialsPage() {
  const qc = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState<Credential | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'credentials'],
    queryFn: () => api.get<ApiSuccess<Credential[]>>('/api/admin/credentials'),
  });

  const toggleM = useMutation({
    mutationFn: (id: string) => api.put(`/api/admin/credentials/${id}/toggle`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'credentials'] }),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/credentials/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'credentials'] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-navy-dark">🏛️ الوثائق والشهادات</h1>
          <p className="text-sm text-gray-500 mt-1">تظهر بالعموم على /credentials بصور موحّدة الأبعاد.</p>
        </div>
        <Button variant="primary" onClick={() => setOpenCreate(true)}>+ إضافة وثيقة</Button>
      </div>

      {isLoading ? (
        <div className="text-gray-500">⟳ جاري التحميل…</div>
      ) : !data?.data?.length ? (
        <Card><CardBody>
          <div className="text-center py-12 text-gray-500">
            <div className="text-5xl mb-3">📜</div>
            لا توجد وثائق بعد. اضغط <b>+ إضافة وثيقة</b> لرفع أوّل ترخيص.
          </div>
        </CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.data.map((c) => (
            <Card key={c.id} className={c.active ? '' : 'opacity-60'}>
              <CardBody className="!p-3">
                <div className="aspect-square w-full bg-gray-50 rounded-lg overflow-hidden mb-3 border">
                  <img src={c.imageUrl} alt={c.title} className="w-full h-full object-contain" />
                </div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge color={CATEGORY[c.category]?.color}>{CATEGORY[c.category]?.label}</Badge>
                  {c.active ? <Badge color="green">مُفعّل</Badge> : <Badge color="gray">معطّل</Badge>}
                  <span className="text-xs text-gray-400">ترتيب: {c.sortOrder}</span>
                </div>
                <h3 className="font-black text-navy-dark mb-1 line-clamp-2">{c.title}</h3>
                {c.referenceNo && <div className="text-xs text-gray-500" dir="ltr">رقم: {c.referenceNo}</div>}
                {c.issuedBy && <div className="text-xs text-gray-500">📍 {c.issuedBy}</div>}
                <div className="flex gap-1 mt-3">
                  <Button size="sm" variant="ghost" onClick={() => toggleM.mutate(c.id)}>{c.active ? '⏸ تعطيل' : '▶︎ تفعيل'}</Button>
                  <Button size="sm" variant="primary" onClick={() => setEditing(c)}>تعديل</Button>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm('حذف الوثيقة نهائياً؟')) deleteM.mutate(c.id); }}>🗑</Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {openCreate && <CredentialModal mode="create" onClose={() => setOpenCreate(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'credentials'] })} />}
      {editing && <CredentialModal mode="edit" item={editing} onClose={() => setEditing(null)} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'credentials'] })} />}
    </div>
  );
}

function CredentialModal({ mode, item, onClose, onSaved }: {
  mode: 'create' | 'edit';
  item?: Credential;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState({
    title:       item?.title       ?? '',
    description: item?.description ?? '',
    imageUrl:    item?.imageUrl    ?? '',
    category:    (item?.category   ?? 'LICENSE') as Credential['category'],
    sortOrder:   item?.sortOrder   ?? 0,
    active:      item?.active      ?? true,
    issuedBy:    item?.issuedBy    ?? '',
    issuedAt:    item?.issuedAt    ? item.issuedAt.slice(0, 10) : '',
    expiresAt:   item?.expiresAt   ? item.expiresAt.slice(0, 10) : '',
    referenceNo: item?.referenceNo ?? '',
  });
  const [err, setErr] = useState('');

  const m = useMutation({
    mutationFn: () => {
      const payload = {
        title: f.title.trim(),
        description: f.description.trim() || null,
        imageUrl: f.imageUrl,
        category: f.category,
        sortOrder: Number(f.sortOrder) || 0,
        active: f.active,
        issuedBy: f.issuedBy.trim() || null,
        issuedAt: f.issuedAt || null,
        expiresAt: f.expiresAt || null,
        referenceNo: f.referenceNo.trim() || null,
      };
      return mode === 'create'
        ? api.post('/api/admin/credentials', payload)
        : api.put(`/api/admin/credentials/${item!.id}`, payload);
    },
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });

  const validate = () => {
    if (!f.title.trim() || f.title.trim().length < 2) return 'الاسم مطلوب (٢ حرف فأكثر)';
    if (!f.imageUrl)                                  return 'الصورة مطلوبة';
    return null;
  };
  const submit = () => {
    const v = validate();
    if (v) { setErr(v); return; }
    setErr('');
    m.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">
            {mode === 'create' ? '+ إضافة وثيقة' : '✏️ تعديل وثيقة'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <Input
            label="اسم الوثيقة *"
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
            placeholder="مثال: السجل التجاري لشركة صورة العقارية"
          />

          <div>
            <label className="block text-sm font-bold text-ink mb-1">صورة الوثيقة *</label>
            <p className="text-xs text-gray-500 mb-2">يُفضّل صورة مربّعة أو مقصوصة بأبعاد متساوية (مثل ١٠٠٠×١٠٠٠).</p>
            <ImageUploader
              value={f.imageUrl}
              onChange={(url) => setF({ ...f, imageUrl: url ?? '' })}
              purpose="credential"
              shape="square"
              size="lg"
              hint="JPG / PNG / WebP — حتى ٥MB"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-ink mb-1">التصنيف</label>
              <select
                value={f.category}
                onChange={(e) => setF({ ...f, category: e.target.value as Credential['category'] })}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
              >
                <option value="LICENSE">🪪 رخصة</option>
                <option value="COMMERCIAL_REGISTER">📋 سجل تجاري</option>
                <option value="CERTIFICATE">🏅 شهادة</option>
                <option value="OTHER">📄 أخرى</option>
              </select>
            </div>
            <Input
              label="ترتيب العرض"
              type="number"
              value={String(f.sortOrder)}
              onChange={(e) => setF({ ...f, sortOrder: Number(e.target.value) })}
              hint="الأقل يظهر أولاً"
            />
            <Input
              label="الجهة المُصدِرة (اختياري)"
              value={f.issuedBy}
              onChange={(e) => setF({ ...f, issuedBy: e.target.value })}
              placeholder="مثال: وزارة التجارة"
            />
            <Input
              label="رقم الوثيقة (اختياري)"
              value={f.referenceNo}
              onChange={(e) => setF({ ...f, referenceNo: e.target.value })}
              dir="ltr"
            />
            <Input
              label="تاريخ الإصدار (اختياري)"
              type="date"
              value={f.issuedAt}
              onChange={(e) => setF({ ...f, issuedAt: e.target.value })}
            />
            <Input
              label="تاريخ الانتهاء (اختياري)"
              type="date"
              value={f.expiresAt}
              onChange={(e) => setF({ ...f, expiresAt: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-ink mb-1">وصف (اختياري)</label>
            <textarea
              value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
              placeholder="نبذة عن الوثيقة وأهميتها"
            />
          </div>

          <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
            <input
              type="checkbox"
              checked={f.active}
              onChange={(e) => setF({ ...f, active: e.target.checked })}
              className="w-5 h-5 accent-navy"
            />
            <span>عرض الوثيقة على الموقع العام</span>
          </label>

          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={submit} loading={m.isPending}>💾 حفظ</Button>
        </div>
      </div>
    </div>
  );
}
